import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { Brand } from '../Brand';
import { HumanizerStudio } from '../HumanizerStudio';
import { api, isApiConfigured, type Job, type Package, type Usage } from '../lib/api';
import { hrefForPage, type AppPage } from '../lib/routing';
import { loadPrefs, savePrefs, type HumanizerPrefs, type Strength, type Tone } from '../lib/prefs';

type DashboardProps = {
  page: AppPage;
  user: User;
  session: Session;
  role: string;
  usage: Usage | null;
  jobs: Job[];
  packages: Package[];
  onNavigate: (to: string) => void;
  onSignOut: () => void;
  onJobsChange: (jobs: Job[]) => void;
  onUsageChange: (usage: Usage) => void;
  notify: (message: string) => void;
};

const SIDEBAR_KEY = 'phrasio-sidebar-collapsed';

const navItems: Array<{ page: AppPage; label: string; hint: string; icon: ReactNode }> = [
  { page: 'humanizer', label: 'Humanizer', hint: 'Rewrite a draft', icon: '✦' },
  { page: 'history', label: 'History', hint: 'Past rewrites', icon: <HistoryIcon /> },
  { page: 'settings', label: 'Settings', hint: 'Voice and Meaning Guard', icon: '⚙' },
  { page: 'profile', label: 'Profile', hint: 'Account and plan', icon: '◉' },
];

function HistoryIcon() {
  return (
    <svg className="dash-nav-svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4.5 12a7.5 7.5 0 1 0 2.2-5.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M4.5 5.5v4.2h4.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 8.5V12l2.6 1.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function planLabel(usage: Usage | null) {
  if (!usage || usage.total_granted === 0) return 'Free';
  return 'Student pack';
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="dash-metric">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

export function Dashboard({
  page,
  user,
  session,
  role,
  usage,
  jobs,
  packages,
  onNavigate,
  onSignOut,
  onJobsChange,
  onUsageChange,
  notify,
}: DashboardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === '1'; } catch { return false; }
  });
  const wordsProcessed = useMemo(
    () => jobs.filter((job) => job.status === 'completed').reduce((sum, job) => sum + (job.word_count || 0), 0),
    [jobs],
  );

  const toggleCollapsed = () => {
    setCollapsed((current) => {
      const next = !current;
      try { localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  const go = (to: string) => {
    onNavigate(to);
    setMenuOpen(false);
  };

  return (
    <div className={`dash ${collapsed ? 'collapsed' : ''}`}>
      <aside className={`dash-sidebar ${menuOpen ? 'open' : ''}`}>
        <div className="dash-sidebar-head">
          <a className="dash-brand" href="/" aria-label="Phrasio home"><Brand /></a>
          <button
            className="dash-collapse"
            type="button"
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={toggleCollapsed}
          >
            {collapsed ? '»' : '«'}
          </button>
        </div>
        <nav className="dash-nav" aria-label="Dashboard">
          {navItems.map((item) => (
            <a
              key={item.page}
              href={hrefForPage(item.page)}
              className={page === item.page ? 'active' : ''}
              title={item.label}
              onClick={(event) => { event.preventDefault(); go(hrefForPage(item.page)); }}
            >
              <span className="dash-nav-icon" aria-hidden="true">{item.icon}</span>
              <span className="dash-nav-copy">
                <strong>{item.label}</strong>
                <small>{item.hint}</small>
              </span>
            </a>
          ))}
          {role === 'admin' && (
            <a href="/admin" className={page === 'admin' ? 'active' : ''} title="Admin" onClick={(event) => { event.preventDefault(); go('/admin'); }}>
              <span className="dash-nav-icon" aria-hidden="true">▣</span>
              <span className="dash-nav-copy">
                <strong>Admin</strong>
                <small>Operations</small>
              </span>
            </a>
          )}
        </nav>
        <div className="dash-sidebar-foot">
          <p className="dash-user" title={user.email ?? ''}>{user.email}</p>
          <button className="text-btn" type="button" onClick={onSignOut}>{collapsed ? 'Out' : 'Log out'}</button>
        </div>
      </aside>
      <div className="dash-main">
        <header className="dash-topbar">
          <button className="dash-menu" type="button" aria-label="Open menu" onClick={() => setMenuOpen((open) => !open)}>
            <span /><span /><span />
          </button>
          <button
            className="dash-collapse dash-collapse-main"
            type="button"
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onClick={toggleCollapsed}
          >
            {collapsed ? '»' : '«'}
          </button>
          <span className="dash-top-title">
            {page === 'profile' ? 'Profile' : page === 'settings' ? 'Humanizer settings' : page === 'history' ? 'History' : page === 'admin' ? 'Admin' : 'Humanizer'}
          </span>
        </header>
        {page === 'humanizer' && (
          <HumanizerStudio
            user={user}
            session={session}
            usage={usage}
            onJobsChange={onJobsChange}
            onUsageChange={onUsageChange}
            notify={notify}
          />
        )}
        {page === 'profile' && (
          <ProfileView
            user={user}
            usage={usage}
            jobs={jobs}
            packages={packages}
            wordsProcessed={wordsProcessed}
            session={session}
            notify={notify}
          />
        )}
        {page === 'settings' && <SettingsView notify={notify} />}
        {page === 'history' && <HistoryView jobs={jobs} />}
        {page === 'admin' && <AdminView session={session} role={role} />}
      </div>
      {menuOpen && <button className="dash-scrim" type="button" aria-label="Close menu" onClick={() => setMenuOpen(false)} />}
    </div>
  );
}

function ProfileView({
  user,
  usage,
  jobs,
  packages,
  wordsProcessed,
  session,
  notify,
}: {
  user: User;
  usage: Usage | null;
  jobs: Job[];
  packages: Package[];
  wordsProcessed: number;
  session: Session;
  notify: (message: string) => void;
}) {
  const created = user.created_at ? new Date(user.created_at).toLocaleDateString() : '—';
  const studentPackage = packages.find((item) => item.allowance === 3) ?? packages[0];
  const handleCheckout = async () => {
    if (!studentPackage || !isApiConfigured()) {
      notify('Checkout is not configured yet.');
      return;
    }
    try {
      const checkout = await api.checkout(session, studentPackage.id);
      window.location.assign(checkout.checkout_url);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Checkout is temporarily unavailable.');
    }
  };

  return (
    <section className="dash-page">
      <span className="eyebrow">YOUR ACCOUNT</span>
      <h1>Profile</h1>
      <p className="dash-lead">A snapshot of how you use Phrasio.</p>
      <div className="dash-profile-head">
        <div className="dash-avatar" aria-hidden="true">{(user.email?.[0] || 'P').toUpperCase()}</div>
        <div>
          <strong>{user.email || 'Phrasio member'}</strong>
          <small>Joined {created}</small>
        </div>
      </div>
      <div className="dash-metric-grid">
        <Metric label="Plan" value={planLabel(usage)} />
        <Metric label="Uses remaining" value={usage?.available_uses ?? '—'} />
        <Metric label="Successful rewrites" value={usage?.consumed_uses ?? 0} />
        <Metric label="Words processed" value={wordsProcessed.toLocaleString()} />
      </div>
      <article className="dash-card">
        <h2>Current subscription</h2>
        <p>{planLabel(usage) === 'Free' ? 'You are on the free plan. Buy a Student pack when you need three extra successful rewrites.' : 'Student pack uses are added when Paddle confirms payment. Unused uses stay on this account.'}</p>
        <ul>
          <li>Granted uses: {usage?.total_granted ?? 0}</li>
          <li>In progress: {usage?.reserved_uses ?? 0}</li>
          <li>Drafts in history: {jobs.length}</li>
        </ul>
        <button className="button button-primary" type="button" onClick={() => void handleCheckout()}>Get Student pack</button>
      </article>
    </section>
  );
}

function SettingsView({ notify }: { notify: (message: string) => void }) {
  const [prefs, setPrefs] = useState<HumanizerPrefs>(() => loadPrefs());
  const update = (next: HumanizerPrefs) => {
    setPrefs(next);
    savePrefs(next);
    notify('Settings saved');
  };
  return (
    <section className="dash-page">
      <span className="eyebrow">HUMANIZER SETTINGS</span>
      <h1>Defaults for new drafts</h1>
      <p className="dash-lead">These load when you open the Humanizer. You can still change them on each rewrite.</p>
      <article className="dash-card">
        <h2>Voice</h2>
        <div className="chip-row" role="group" aria-label="Default voice">
          {(['natural', 'academic', 'simple', 'confident'] as Tone[]).map((item) => (
            <button key={item} className={`tone-chip ${prefs.tone === item ? 'active' : ''}`} type="button" onClick={() => update({ ...prefs, tone: item })}>{item[0].toUpperCase() + item.slice(1)}</button>
          ))}
        </div>
      </article>
      <article className="dash-card">
        <h2>Rewrite strength</h2>
        <div className="segmented" role="group" aria-label="Default strength">
          {(['light', 'balanced', 'deep'] as Strength[]).map((item) => (
            <button key={item} className={prefs.strength === item ? 'active' : ''} type="button" onClick={() => update({ ...prefs, strength: item })}>{item[0].toUpperCase() + item.slice(1)}</button>
          ))}
        </div>
      </article>
      <article className="dash-card">
        <h2>Meaning Guard</h2>
        <label className="guard-toggle dash-settings-toggle">
          <span className="guard-icon" aria-hidden="true">✦</span>
          <span><strong>{prefs.guard ? 'On by default' : 'Off by default'}</strong><small>Protect names, numbers, and citations</small></span>
          <input type="checkbox" checked={prefs.guard} onChange={(event) => update({ ...prefs, guard: event.target.checked })} />
          <span className="switch" aria-hidden="true" />
        </label>
      </article>
    </section>
  );
}

function HistoryView({ jobs }: { jobs: Job[] }) {
  return (
    <section className="dash-page">
      <span className="eyebrow">REWRITE HISTORY</span>
      <h1>Your drafts, in one place.</h1>
      <div className="dash-table-wrap">
        <table className="dash-table">
          <thead>
            <tr><th>Draft</th><th>Words</th><th>Status</th><th>Created</th></tr>
          </thead>
          <tbody>
            {jobs.length ? jobs.map((job) => (
              <tr key={job.id}>
                <td data-label="Draft">{job.title || 'Untitled draft'}</td>
                <td data-label="Words">{job.word_count}</td>
                <td data-label="Status"><span className="dash-status">{job.status}</span></td>
                <td data-label="Created">{new Date(job.created_at).toLocaleDateString()}</td>
              </tr>
            )) : <tr><td colSpan={4}>Your queued rewrites will appear here.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AdminView({ session, role }: { session: Session; role: string }) {
  const [metrics, setMetrics] = useState<Record<string, number> | null>(null);
  const [users, setUsers] = useState<Array<Record<string, unknown>>>([]);
  const [jobs, setJobs] = useState<Array<Record<string, unknown>>>([]);
  const [error, setError] = useState('');
  useEffect(() => {
    if (role !== 'admin') return;
    void Promise.all([api.adminMetrics(session), api.adminUsers(session), api.adminJobs(session)]).then(([summary, userRows, jobRows]) => {
      setMetrics(summary); setUsers(userRows.items); setJobs(jobRows.items);
    }).catch((reason: unknown) => setError(reason instanceof Error ? reason.message : 'Admin data could not be loaded.'));
  }, [role, session]);
  if (role !== 'admin') {
    return <section className="dash-page"><h1>Admin</h1><p>You do not have access to this page.</p></section>;
  }
  const refresh = () => {
    void Promise.all([api.adminMetrics(session), api.adminUsers(session), api.adminJobs(session)]).then(([summary, userRows, jobRows]) => {
      setMetrics(summary); setUsers(userRows.items); setJobs(jobRows.items);
    }).catch(() => undefined);
  };
  return (
    <section className="dash-page">
      <span className="eyebrow">OPERATIONS</span>
      <h1>Admin overview</h1>
      {error && <p className="dash-error">{error}</p>}
      <div className="dash-metric-grid">
        {[['Requests', metrics?.total_requests], ['Successes', metrics?.successes], ['Failures', metrics?.failures], ['Quota rejects', metrics?.quota_rejections], ['Revenue (minor)', metrics?.revenue_minor]].map(([label, value]) => (
          <Metric key={String(label)} label={String(label)} value={typeof value === 'number' ? value : '—'} />
        ))}
      </div>
      <div className="dash-admin-grid">
        <article className="dash-card">
          <h2>Users</h2>
          {users.slice(0, 10).map((row) => {
            const id = String(row.id ?? '');
            const disabled = row.disabled === true;
            return (
              <div key={id} className="dash-admin-row">
                <span><strong>{String(row.email ?? '—')}</strong><small>{String(row.role ?? 'user')} · {disabled ? 'disabled' : 'active'}</small></span>
                <span>
                  <button type="button" onClick={() => { void api.adminAdjust(session, id, 1).then(refresh); }}>+1 use</button>
                  <button type="button" onClick={() => { void api.adminDisable(session, id, !disabled).then(refresh); }}>{disabled ? 'Enable' : 'Disable'}</button>
                </span>
              </div>
            );
          })}
          {!users.length && <p>No users yet.</p>}
        </article>
        <article className="dash-card">
          <h2>Recent jobs</h2>
          {jobs.slice(0, 10).map((row, index) => {
            const id = String(row.id ?? index);
            const status = String(row.status ?? 'unknown');
            return (
              <div key={id} className="dash-admin-row">
                <span><strong>{status}</strong><small>{String(row.error_code ?? row.provider ?? '—')}</small></span>
                {(status === 'processing' || status === 'retrying') && <button type="button" onClick={() => { void api.adminRetry(session, id).then(refresh); }}>Retry</button>}
              </div>
            );
          })}
          {!jobs.length && <p>No jobs yet.</p>}
        </article>
      </div>
    </section>
  );
}
